import { X, RotateCcw } from "lucide-react";
import { t } from "@/lib/i18n";

const SLIDERS = [
  { key: "brightness", labelKey: "brightness", min: 50, max: 200, step: 1, unit: "%", default: 100, testid: "brightness-slider" },
  { key: "contrast",   labelKey: "contrast",   min: 50, max: 200, step: 1, unit: "%", default: 100, testid: "contrast-slider" },
  { key: "saturation", labelKey: "saturation", min: 0,  max: 200, step: 1, unit: "%", default: 100, testid: "saturation-slider" },
  { key: "warmth",     labelKey: "warmth",     min: -100, max: 100, step: 1, unit: "", default: 0,  testid: "warmth-slider" },
];

export default function FilterPanel({
  filters,
  activePreset,
  presets,
  onChange,
  onApplyPreset,
  onReset,
  onClose,
  orientation = "portrait",
}) {
  const set = (key, value) =>
    onChange({ ...filters, [key]: Number(value) });

  const isLandscape = orientation !== "portrait";

  return (
    <div
      data-testid="filter-panel"
      className={
        isLandscape
          ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(440px,78vw)] max-h-[88dvh] z-40 rounded-3xl bg-black/70 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] fade-in overflow-y-auto"
          : "absolute left-0 right-0 bottom-[210px] z-40 mx-4 rounded-3xl bg-black/60 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] slide-up overflow-hidden"
      }
    >
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <span className="font-mono-label text-white/90">{t("adjustments")}</span>
        <div className="flex items-center gap-2">
          <button
            data-testid="reset-filters-button"
            onClick={onReset}
            aria-label="Reset"
            className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
          >
            <RotateCcw className="h-3.5 w-3.5 text-white" strokeWidth={1.8} />
          </button>
          <button
            data-testid="close-filters-button"
            onClick={onClose}
            aria-label="Close"
            className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
          >
            <X className="h-3.5 w-3.5 text-white" strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Preset chips */}
      {presets && (
        <div
          data-testid="preset-row"
          className="flex gap-2 overflow-x-auto px-6 pb-3 no-scrollbar"
        >
          {Object.entries(presets).map(([key, p]) => {
            const active = key === activePreset;
            return (
              <button
                key={key}
                data-testid={`preset-${key}`}
                onClick={() => onApplyPreset(key)}
                className={`shrink-0 px-4 h-9 rounded-full font-mono-label transition-all active:scale-95 ${
                  active
                    ? "bg-white text-black border border-white"
                    : "bg-white/5 text-white/80 border border-white/10"
                }`}
              >
                {p.labelKey ? t(p.labelKey) : p.label}
              </button>
            );
          })}
          {activePreset === "custom" && (
            <span
              data-testid="preset-custom-indicator"
              className="shrink-0 px-4 h-9 rounded-full font-mono-label bg-yellow-400/15 text-yellow-300 border border-yellow-400/30 flex items-center"
            >
              {t("presetCustom")}
            </span>
          )}
        </div>
      )}

      <div className="px-6 pb-6 flex flex-col gap-4">
        {SLIDERS.map((s) => {
          const value = filters[s.key];
          const display =
            s.key === "warmth"
              ? value > 0
                ? `+${value}`
                : `${value}`
              : `${value}${s.unit}`;
          return (
            <div key={s.key} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <label className="font-mono-label text-white/75">
                  {t(s.labelKey)}
                </label>
                <span className="font-display text-sm font-medium text-white tabular-nums">
                  {display}
                </span>
              </div>
              <input
                data-testid={s.testid}
                type="range"
                min={s.min}
                max={s.max}
                step={s.step}
                value={value}
                onChange={(e) => set(s.key, e.target.value)}
                className="slider-track"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
